/**
 * EventBus - Pub/sub event system for game hooks.
 *
 * Content packs use the event bus to:
 * - Subscribe to game events (on, once)
 * - Emit events for other systems to react to
 * - Modify event data before handlers receive it (modifiers)
 */

/**
 * Event handler function type.
 */
export type EventHandler<T = unknown> = (data: T) => void | Promise<void>;

/**
 * Event modifier function type.
 * Modifiers can transform event data before handlers receive it.
 */
export type EventModifier<T = unknown> = (data: T) => T;

/**
 * Unsubscribe function returned by on/once/modify.
 */
export type Unsubscribe = () => void;

/**
 * Internal handler storage.
 */
interface HandlerEntry<T = unknown> {
  handler: EventHandler<T>;
  once: boolean;
}

/**
 * Internal modifier storage.
 */
interface ModifierEntry<T = unknown> {
  modifier: EventModifier<T>;
  priority: number;
}

/**
 * EventBus provides pub/sub messaging for game systems.
 *
 * @example
 * ```ts
 * const events = new EventBus();
 *
 * // Subscribe to events
 * const unsubscribe = events.on('xp:gain', (data) => {
 *   console.log(`Gained ${data.amount} XP in ${data.skillId}`);
 * });
 *
 * // Modify events before handlers see them
 * events.modify('xp:gain', (data) => ({
 *   ...data,
 *   amount: data.amount * 1.1 // 10% XP boost
 * }), 10); // priority 10 runs before default (0)
 *
 * // Emit events
 * events.emit('xp:gain', { skillId: 'mining', amount: 100 });
 *
 * // Clean up
 * unsubscribe();
 * ```
 */
export class EventBus {
  /** Handlers by event name */
  private handlers = new Map<string, Set<HandlerEntry>>();

  /** Modifiers by event name, sorted by priority */
  private modifiers = new Map<string, ModifierEntry[]>();

  /**
   * Subscribe to an event.
   *
   * @param event - Event name
   * @param handler - Handler function
   * @returns Unsubscribe function
   */
  on<T>(event: string, handler: EventHandler<T>): Unsubscribe {
    return this.addHandler(event, handler, false);
  }

  /**
   * Subscribe to an event once (auto-unsubscribes after first call).
   *
   * @param event - Event name
   * @param handler - Handler function
   * @returns Unsubscribe function
   */
  once<T>(event: string, handler: EventHandler<T>): Unsubscribe {
    return this.addHandler(event, handler, true);
  }

  /**
   * Unsubscribe a handler from an event.
   *
   * @param event - Event name
   * @param handler - Handler function to remove
   */
  off<T>(event: string, handler: EventHandler<T>): void {
    const handlers = this.handlers.get(event);
    if (!handlers) {
      return;
    }

    for (const entry of handlers) {
      if (entry.handler === handler) {
        handlers.delete(entry);
        break;
      }
    }

    if (handlers.size === 0) {
      this.handlers.delete(event);
    }
  }

  /**
   * Register an event modifier.
   *
   * Modifiers transform event data before handlers receive it.
   * Higher priority modifiers run first.
   *
   * @param event - Event name
   * @param modifier - Modifier function
   * @param priority - Priority (higher = runs first, default 0)
   * @returns Unsubscribe function
   */
  modify<T>(event: string, modifier: EventModifier<T>, priority = 0): Unsubscribe {
    let modifierList = this.modifiers.get(event);
    if (!modifierList) {
      modifierList = [];
      this.modifiers.set(event, modifierList);
    }

    const entry: ModifierEntry = {
      modifier: modifier as EventModifier,
      priority,
    };

    modifierList.push(entry);

    // Sort by priority (descending - higher priority first)
    modifierList.sort((a, b) => b.priority - a.priority);

    return () => {
      const list = this.modifiers.get(event);
      if (list) {
        const index = list.indexOf(entry);
        if (index !== -1) {
          list.splice(index, 1);
        }
        if (list.length === 0) {
          this.modifiers.delete(event);
        }
      }
    };
  }

  /**
   * Emit an event.
   *
   * Data is passed through all modifiers first (in priority order),
   * then to all handlers.
   *
   * @param event - Event name
   * @param data - Event data
   */
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  emit<T>(event: string, data: T): void {
    // Apply modifiers
    let modifiedData = data;
    const modifierList = this.modifiers.get(event);
    if (modifierList) {
      for (const entry of modifierList) {
        modifiedData = entry.modifier(modifiedData) as T;
      }
    }

    // Call handlers
    const handlers = this.handlers.get(event);
    if (!handlers) {
      return;
    }

    const toRemove: HandlerEntry[] = [];

    for (const entry of handlers) {
      try {
        void entry.handler(modifiedData);
      } catch (error) {
        console.error(`Error in event handler for "${event}":`, error);
      }

      if (entry.once) {
        toRemove.push(entry);
      }
    }

    // Remove one-time handlers
    for (const entry of toRemove) {
      handlers.delete(entry);
    }

    if (handlers.size === 0) {
      this.handlers.delete(event);
    }
  }

  /**
   * Emit an event and wait for all async handlers.
   *
   * @param event - Event name
   * @param data - Event data
   */
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  async emitAsync<T>(event: string, data: T): Promise<void> {
    // Apply modifiers
    let modifiedData = data;
    const modifierList = this.modifiers.get(event);
    if (modifierList) {
      for (const entry of modifierList) {
        modifiedData = entry.modifier(modifiedData) as T;
      }
    }

    // Call handlers
    const handlers = this.handlers.get(event);
    if (!handlers) {
      return;
    }

    const toRemove: HandlerEntry[] = [];
    const promises: Promise<void>[] = [];

    for (const entry of handlers) {
      try {
        const result = entry.handler(modifiedData);
        if (result instanceof Promise) {
          promises.push(result);
        }
      } catch (error) {
        console.error(`Error in event handler for "${event}":`, error);
      }

      if (entry.once) {
        toRemove.push(entry);
      }
    }

    // Wait for all async handlers
    await Promise.all(promises);

    // Remove one-time handlers
    for (const entry of toRemove) {
      handlers.delete(entry);
    }

    if (handlers.size === 0) {
      this.handlers.delete(event);
    }
  }

  /**
   * Check if an event has any handlers.
   *
   * @param event - Event name
   */
  hasHandlers(event: string): boolean {
    const handlers = this.handlers.get(event);
    return handlers !== undefined && handlers.size > 0;
  }

  /**
   * Get handler count for an event.
   *
   * @param event - Event name
   */
  handlerCount(event: string): number {
    return this.handlers.get(event)?.size ?? 0;
  }

  /**
   * Clear all handlers and modifiers for an event.
   *
   * @param event - Event name
   */
  clear(event: string): void {
    this.handlers.delete(event);
    this.modifiers.delete(event);
  }

  /**
   * Reset the event bus completely.
   */
  reset(): void {
    this.handlers.clear();
    this.modifiers.clear();
  }

  /**
   * Add a handler to an event.
   */
  private addHandler<T>(event: string, handler: EventHandler<T>, once: boolean): Unsubscribe {
    let handlers = this.handlers.get(event);
    if (!handlers) {
      handlers = new Set();
      this.handlers.set(event, handlers);
    }

    const entry: HandlerEntry = {
      handler: handler as EventHandler,
      once,
    };

    handlers.add(entry);

    return () => {
      handlers.delete(entry);
      if (handlers.size === 0) {
        this.handlers.delete(event);
      }
    };
  }
}
